import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "@/lib/auth";
import { queryClient } from "@/lib/queryClient";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "next-themes";
import { ConfirmDialogProvider } from "@/components/ConfirmDialog";
import { UnsavedChangesRegistryProvider, UnsavedChangesLinkInterceptor } from "@/lib/useUnsavedChanges";
import AppShell from "@/components/layout/AppShell";
import CommandPalette from "@/components/CommandPalette";
import GlobalOpportunityDrawer from "@/components/GlobalOpportunityDrawer";
import { TickerDrawerProvider } from "@/components/TickerDrawer";
import RebalanceBucketsFlow from "@/components/RebalanceBucketsFlow";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Proposal from "@/pages/Proposal";
import Positions from "@/pages/Positions";
import Journal from "@/pages/Journal";
import Learning from "@/pages/Learning";
import Settings from "@/pages/Settings";
import ScannerPolicy from "@/pages/ScannerPolicy";
import Opportunities from "@/pages/Opportunities";
import Catalysts from "@/pages/Catalysts";
import PortfolioDashboard from "@/pages/PortfolioDashboard";
import AnalysisHistory from "@/pages/AnalysisHistory";
import Audit from "@/pages/Audit";
import TradeReplay from "@/pages/TradeReplay";
import PerformanceLab from "@/pages/PerformanceLab";
import Scanner from "@/pages/Scanner";
import SystemPage from "@/pages/SystemPage";
import TradingActivity from "@/pages/TradingActivity";
import AIQuality from "@/pages/AIQuality";
import "@/App.css";

function Private({ children }) {
  const { user } = useAuth();
  if (!localStorage.getItem("tm_token")) return <Navigate to="/login" replace/>;
  return (
    <AppShell>
      {children}
      <CommandPalette/>
      <GlobalOpportunityDrawer/>
    </AppShell>
  );
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false} forcedTheme="dark">
      <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <ConfirmDialogProvider>
          <UnsavedChangesRegistryProvider>
          <TickerDrawerProvider>
          <UnsavedChangesLinkInterceptor />
          <RebalanceBucketsFlow />
          <Routes>
            <Route path="/login" element={<Login/>}/>
            <Route path="/register" element={<Register/>}/>
            <Route path="/opportunities" element={<Private><Opportunities/></Private>}/>
            <Route path="/catalysts" element={<Private><Catalysts/></Private>}/>
            <Route path="/portfolios" element={<Private><PortfolioDashboard/></Private>}/>
            <Route path="/dashboard" element={<Private><Dashboard/></Private>}/>
            <Route path="/proposal/:id" element={<Private><Proposal/></Private>}/>
            <Route path="/positions" element={<Private><Positions/></Private>}/>
            <Route path="/activity" element={<Private><TradingActivity/></Private>}/>
            <Route path="/ai-quality" element={<Private><AIQuality/></Private>}/>
            <Route path="/scanner-policy" element={<Private><ScannerPolicy/></Private>}/>
            <Route path="/journal" element={<Private><Journal/></Private>}/>
            <Route path="/analysis" element={<Private><AnalysisHistory/></Private>}/>
            <Route path="/analysis-history" element={<Private><AnalysisHistory/></Private>}/>
            <Route path="/learning" element={<Private><Learning/></Private>}/>
            <Route path="/settings" element={<Private><Settings/></Private>}/>
            <Route path="/system" element={<Private><SystemPage/></Private>}/>
            <Route path="/audit" element={<Private><Audit/></Private>}/>
            <Route path="/replay/:positionId" element={<Private><TradeReplay/></Private>}/>
            <Route path="/performance" element={<Private><PerformanceLab/></Private>}/>
            <Route path="/scanner" element={<Private><Scanner/></Private>}/>
            <Route path="*" element={<Navigate to="/opportunities" replace/>}/>
          </Routes>
          <Toaster position="bottom-right"/>
          </TickerDrawerProvider>
          </UnsavedChangesRegistryProvider>
          </ConfirmDialogProvider>
        </BrowserRouter>
      </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
